import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { 
  Trophy, Gift, Sparkles, Frown, Star, Heart, Zap, 
  ShoppingBag, Tag, Percent, DollarSign, Truck, 
  Plus, Trash2, Save, Edit2, RotateCcw, ArrowLeft,
  CheckCircle, AlertCircle, Loader2, Link, Copy,
  Users, X, Calendar, Search, Info, Globe, Image as ImageIcon, Check 
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../utils/api'; 
import { useAuth } from '../../components/context/AuthContext'; 
import { useSettings } from '../../components/context/SettingsContext'; 
import { format, isValid } from 'date-fns'; 
import { toZonedTime } from 'date-fns-tz'; 

// --- ASSETS & CONFIG ---
const ICON_MAP = {
  Gift, Trophy, Sparkles, Star, Heart, Zap, 
  ShoppingBag, Tag, Percent, DollarSign, Truck, Frown
};

const COLORS = [
  { name: 'blue', bg: 'bg-indigo-50 dark:bg-indigo-500/10', text: 'text-indigo-600 dark:text-indigo-400', ring: 'ring-indigo-400' },
  { name: 'green', bg: 'bg-emerald-50 dark:bg-emerald-500/10', text: 'text-emerald-600 dark:text-emerald-400', ring: 'ring-emerald-400' },
  { name: 'purple', bg: 'bg-purple-50 dark:bg-purple-500/10', text: 'text-purple-600 dark:text-purple-400', ring: 'ring-purple-400' },
  { name: 'yellow', bg: 'bg-amber-50 dark:bg-amber-500/10', text: 'text-amber-600 dark:text-amber-400', ring: 'ring-amber-400' },
  { name: 'red', bg: 'bg-rose-50 dark:bg-rose-500/10', text: 'text-rose-600 dark:text-rose-400', ring: 'ring-rose-400' },
  { name: 'gray', bg: 'bg-slate-100 dark:bg-slate-500/10', text: 'text-slate-600 dark:text-slate-400', ring: 'ring-slate-400' },
];

const initialPrizeTemplate = [
    { id: 'p_1', title: '10% OFF', description: 'On your next service', icon: 'Percent', color: 'blue', code: 'WELCOME10', probability: 50 },
    { id: 'p_2', title: 'Free Call Out', description: 'Save £60 instantly', icon: 'Truck', color: 'green', code: 'FREECALL', probability: 20 },
    { id: 'p_3', title: 'No Prize', description: 'Better luck next time!', icon: 'Frown', color: 'gray', code: '', probability: 30 },
];

// ⭐ NEW HELPER: Generic Image Upload Component (Modernised)
const ImageUploadInput = ({ label, value, onChange, id }) => {
    const [isUploading, setIsUploading] = useState(false);

    const handleUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            toast.error('Please upload an image file (PNG, JPG).');
            return;
        }

        const formData = new FormData();
        formData.append('file', file);

        setIsUploading(true);
        try {
            const res = await api.post('/forms/upload-asset', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            onChange(res.data.url);
            toast.success(`${label} uploaded!`);
        } catch (err) {
            console.error("Upload failed:", err);
            toast.error(`Failed to upload ${label}.`);
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <div>
            <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-1.5 uppercase tracking-wider">{label}</label>
            <div className="flex items-center gap-4">
                <div className="relative w-full">
                    <input 
                        id={id}
                        type="file" 
                        accept="image/*"
                        onChange={handleUpload}
                        disabled={isUploading}
                        className="hidden"
                    />
                    <label 
                        htmlFor={id}
                        className={`flex items-center justify-center w-full px-4 py-3 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-2xl cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors ${isUploading ? 'opacity-50' : ''}`}
                    >
                        {isUploading ? (
                            <span className="text-sm font-bold text-slate-500 flex items-center">
                                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Uploading...
                            </span>
                        ) : (
                            <span className="text-sm font-bold text-slate-500 dark:text-slate-400 flex items-center">
                                <ImageIcon className="w-5 h-5 mr-2 opacity-70" /> 
                                {value ? 'Change Image' : 'Upload Image'}
                            </span>
                        )}
                    </label>
                </div>
                {value && (
                    <div className="relative w-12 h-12 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 flex-shrink-0 shadow-sm">
                        <img src={value} alt="Preview" className="w-full h-full object-cover" />
                    </div>
                )}
            </div>
        </div>
    );
};


// --- INTERNAL COMPONENT: RESULTS MODAL (Modernised) ---
const ResultsModal = ({ gameId, isOpen, onClose, formatDate }) => {
    const { data: gameDetails, isLoading } = useQuery({
        queryKey: ['scratchCardResults', gameId],
        queryFn: async () => {
            const res = await api.get(`/scratch-cards/${gameId}`);
            return res.data;
        },
        enabled: isOpen && !!gameId,
    });

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm sm:p-4 animate-in fade-in zoom-in-95 duration-200">
            <div className="relative flex flex-col w-full h-[100dvh] sm:h-auto sm:max-h-[90vh] max-w-4xl bg-white dark:bg-slate-800 sm:rounded-3xl shadow-2xl overflow-hidden border border-transparent dark:border-slate-700/50 transition-all">
                
                {/* Header */}
                <div className="px-6 py-6 border-b border-slate-100 dark:border-slate-700/50 flex justify-between items-center bg-white dark:bg-slate-800 shrink-0 z-20">
                    <div className="flex items-center gap-4">
                        <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full bg-indigo-50 dark:bg-indigo-900/30">
                            <Users className="h-7 w-7 text-indigo-600 dark:text-indigo-400" />
                        </div>
                        <div>
                            <h2 className="text-xl font-extrabold text-slate-900 dark:text-white tracking-tight">
                                {isLoading ? 'Loading Results...' : `${gameDetails?.name || 'Game'} Results`}
                            </h2>
                            <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mt-0.5">
                                Player history and outcome tracking
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 dark:hover:text-slate-200 rounded-full transition-colors active:scale-95">
                        <X size={24} />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto custom-scrollbar p-4 sm:p-6 bg-slate-50/50 dark:bg-slate-900/20">
                    {isLoading ? (
                         <div className="flex flex-col items-center justify-center py-16">
                            <Loader2 className="w-10 h-10 animate-spin text-indigo-500 mb-4" />
                            <p className="text-xs font-extrabold text-slate-400 tracking-widest uppercase animate-pulse">Loading Players...</p>
                        </div>
                    ) : !gameDetails?.history || gameDetails.history.length === 0 ? (
                        <div className="text-center py-16 px-4 bg-white dark:bg-slate-800 border border-slate-200/60 dark:border-slate-700/50 rounded-3xl shadow-sm">
                            <div className="p-4 bg-slate-50 dark:bg-slate-900/50 rounded-full inline-block mb-4 shadow-inner">
                                <Users className="w-10 h-10 text-slate-400" />
                            </div>
                            <p className="text-lg font-extrabold text-slate-900 dark:text-white mb-1">No players yet</p>
                            <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Share your campaign link to start capturing leads!</p>
                        </div>
                    ) : (
                        <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-sm border border-slate-200/60 dark:border-slate-700/50 overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm text-left border-collapse">
                                    <thead className="bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700/50">
                                        <tr>
                                            <th className="px-6 py-4 text-xs font-black tracking-widest text-slate-500 dark:text-slate-400 uppercase">Date</th>
                                            <th className="px-6 py-4 text-xs font-black tracking-widest text-slate-500 dark:text-slate-400 uppercase">Player</th>
                                            <th className="px-6 py-4 text-xs font-black tracking-widest text-slate-500 dark:text-slate-400 uppercase">Status</th>
                                            <th className="px-6 py-4 text-xs font-black tracking-widest text-slate-500 dark:text-slate-400 uppercase">Outcome</th>
                                            <th className="px-6 py-4 text-xs font-black tracking-widest text-slate-500 dark:text-slate-400 uppercase">Prize Code</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60">
                                        {[...gameDetails.history].reverse().map((entry, idx) => (
                                            <tr key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-800/80 transition-colors">
                                                <td className="px-6 py-4 text-slate-500 dark:text-slate-400 font-medium whitespace-nowrap">
                                                    {entry.claimedAt ? formatDate(entry.claimedAt) : '-'}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="font-bold text-slate-900 dark:text-white">{entry.name}</div>
                                                    <div className="text-xs font-medium text-slate-500">{entry.email}</div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    {entry.customerType === 'Existing Customer' ? (
                                                        <span className="inline-flex items-center px-3 py-1 rounded-lg text-xs font-bold bg-purple-50 text-purple-700 border border-purple-200/50 dark:bg-purple-500/10 dark:text-purple-400 dark:border-purple-800/50">
                                                            Existing Customer
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center px-3 py-1 rounded-lg text-xs font-bold bg-indigo-50 text-indigo-700 border border-indigo-200/50 dark:bg-indigo-500/10 dark:text-indigo-400 dark:border-indigo-800/50">
                                                            <Star className="w-3 h-3 mr-1.5" /> New Lead
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className={`font-bold ${entry.isWinner ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-500'}`}>
                                                        {entry.prizeTitle}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 font-mono font-bold text-slate-600 dark:text-slate-300">
                                                    {entry.prizeCode || '-'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
                
                {/* Footer */}
                <div className="shrink-0 bg-slate-50 dark:bg-slate-900/80 px-6 py-5 border-t border-slate-100 dark:border-slate-700/50 flex justify-end z-10">
                    <button 
                        onClick={onClose} 
                        className="w-full sm:w-auto px-6 py-3.5 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 font-bold rounded-xl border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 transition-all shadow-sm active:scale-95"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};


// --- SUB-COMPONENT: PREVIEW CARD (unchanged logic, modernised container) ---
const ScratchCardPreview = ({ prize, isRevealed, onReveal, onReset }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [isScratching, setIsScratching] = useState(false);

  const IconComponent = ICON_MAP[prize.icon] || Gift;
  const colorTheme = COLORS.find(c => c.name === prize.color) || COLORS[0];

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    const container = containerRef.current;

    if (!canvas || !container) return;

    if (!isRevealed) {
        const width = container.offsetWidth;
        const height = container.offsetHeight;
        
        const dpr = window.devicePixelRatio || 1;
        canvas.width = width * dpr;
        canvas.height = height * dpr;
        ctx.scale(dpr, dpr);
        canvas.style.width = `${width}px`;
        canvas.style.height = `${height}px`;

        const gradient = ctx.createLinearGradient(0, 0, width, height);
        gradient.addColorStop(0, '#CBD5E1'); // slate-300
        gradient.addColorStop(0.5, '#E2E8F0'); // slate-200
        gradient.addColorStop(1, '#CBD5E1'); 
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, width, height);
        
        for (let i = 0; i < 600; i++) {
            ctx.fillStyle = Math.random() > 0.5 ? '#94A3B8' : '#F8FAFC';
            ctx.fillRect(Math.random() * width, Math.random() * height, 2, 2);
        }

        ctx.font = 'bold 20px sans-serif';
        ctx.fillStyle = '#64748B'; 
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('SCRATCH HERE', width / 2, height / 2);
        
        ctx.font = '24px serif';
        ctx.fillText('✨', width / 2, (height / 2) - 30);

    } else {
        const width = canvas.width;
        const height = canvas.height;
        ctx.clearRect(0, 0, width, height);
    }
  }, [isRevealed, prize]);

  const scratch = useCallback((x, y) => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    const offsetX = (x - rect.left) * scaleX;
    const offsetY = (y - rect.top) * scaleY;

    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc(offsetX / (window.devicePixelRatio||1), offsetY / (window.devicePixelRatio||1), 25, 0, Math.PI * 2);
    ctx.fill();
  }, []);

  const handleInteract = (e) => {
    if (isRevealed) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    if (e.type === 'mousedown' || e.type === 'touchstart') setIsScratching(true);
    
    if (isScratching || e.type === 'mousedown' || e.type === 'touchstart') {
        scratch(clientX, clientY);
    }
  };

  return (
    <div className="flex flex-col items-center">
       <div 
        ref={containerRef}
        className="relative w-64 h-64 rounded-3xl shadow-xl overflow-hidden border-[6px] border-white dark:border-slate-700 select-none bg-white dark:bg-slate-800 ring-1 ring-slate-200 dark:ring-slate-900"
        onMouseDown={handleInteract}
        onMouseMove={handleInteract}
        onMouseUp={() => setIsScratching(false)}
        onMouseLeave={() => setIsScratching(false)}
        onTouchStart={handleInteract}
        onTouchMove={handleInteract}
        onTouchEnd={() => setIsScratching(false)}
      >
        {/* HIDDEN PRIZE CONTENT */}
        <div className={`absolute inset-0 flex flex-col items-center justify-center p-5 text-center bg-slate-50 dark:bg-slate-800`}>
            <div className={`p-4 rounded-2xl mb-4 transition-transform duration-500 shadow-inner ${isRevealed ? 'scale-110' : 'scale-100'} ${colorTheme.bg}`}>
                <IconComponent className={`w-10 h-10 ${colorTheme.text}`} />
            </div>
            <h4 className="text-xl font-black text-slate-900 dark:text-white leading-tight mb-1.5">{prize.title || 'Mystery Prize'}</h4>
            <p className="text-sm font-medium text-slate-500 dark:text-slate-400 leading-snug">{prize.description || 'Scratch to reveal your reward'}</p>
            
            {isRevealed && prize.code && (
                 <div className="mt-4 px-4 py-1.5 bg-slate-100 dark:bg-slate-700/50 text-slate-700 dark:text-slate-200 text-xs font-mono font-bold rounded-xl border border-dashed border-slate-300 dark:border-slate-500">
                    Code: <span className="select-all text-indigo-600 dark:text-indigo-400">{prize.code}</span>
                 </div>
            )}
        </div>

        {/* OVERLAY CANVAS */}
        <canvas
          ref={canvasRef}
          className={`absolute inset-0 touch-none cursor-crosshair transition-opacity duration-700 ${isRevealed ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
        />
      </div>
      
      <div className="mt-6 flex items-center gap-4 bg-white dark:bg-slate-800 px-4 py-2 rounded-full border border-slate-200 dark:border-slate-700 shadow-sm">
         <button 
            onClick={onReveal} 
            className="text-xs font-bold text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 transition-colors uppercase tracking-wider"
         >
            Force Reveal
         </button>
         <div className="h-4 w-px bg-slate-200 dark:bg-slate-700"></div>
         <button 
            onClick={onReset} 
            className="text-xs font-bold text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 flex items-center transition-colors uppercase tracking-wider"
         >
            <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reset Card
         </button>
      </div>
    </div>
  );
};


// --- MAIN PAGE: SCRATCH CARD MANAGER ---
const ScratchCardManager = () => {
    const queryClient = useQueryClient();
    // Add default empty objects so destructuring never fails
    const { user } = useAuth() || {}; 
    const { settings } = useSettings() || {}; 
    const companyTimezone = settings?.timezone || 'UTC';

    const isUS = useMemo(() => companyTimezone.startsWith('America/'), [companyTimezone]);

    const formatDate = useCallback((dateString, includeTime = true) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        if (!isValid(date)) return 'Invalid date';

        const zonedDate = toZonedTime(date, companyTimezone);
        const dateFormat = isUS ? 'MM/dd/yyyy' : 'dd/MM/yyyy';
        
        if (includeTime) {
            const timeFormat = isUS ? 'hh:mm a' : 'HH:mm';
            const formatString = `${dateFormat} ${timeFormat}`;
            return format(zonedDate, formatString);
        }
        return format(zonedDate, dateFormat);
    }, [isUS, companyTimezone]);

    // Builder State
    const [isBuilderMode, setIsBuilderMode] = useState(false);
    const [editingGame, setEditingGame] = useState(null); 
    const [prizes, setPrizes] = useState(initialPrizeTemplate);
    const [gameName, setGameName] = useState('');
    const [urlSlug, setUrlSlug] = useState(''); 
    const [isActive, setIsActive] = useState(true);
    
    const [seoTitle, setSeoTitle] = useState('');
    const [seoDescription, setSeoDescription] = useState('');
    const [mainPhotoUrl, setMainPhotoUrl] = useState('');
    const [faviconUrl, setFaviconUrl] = useState('');

    const [viewingResultsGameId, setViewingResultsGameId] = useState(null);
    const [copiedGameId, setCopiedGameId] = useState(null);

    const [activePreviewId, setActivePreviewId] = useState(initialPrizeTemplate[0].id);
    const [previewRevealed, setPreviewRevealed] = useState(false);

    // API
    const { data: games = [], isLoading, isError } = useQuery({
        queryKey: ['scratchCards'],
        queryFn: async () => {
            const res = await api.get('/scratch-cards');
            return res.data;
        },
        staleTime: 60000, 
    });

    const createGameMutation = useMutation({
        mutationFn: (newGame) => api.post('/scratch-cards', newGame),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['scratchCards'] });
            toast.success("New Scratch Card Game created successfully!");
            setIsBuilderMode(false);
        },
        onError: (error) => {
            const message = error.response?.data?.message || 'Failed to create game.';
            toast.error(`Error: ${message}`);
        }
    });

    const updateGameMutation = useMutation({
        mutationFn: (updatedGame) => api.put(`/scratch-cards/${updatedGame.id}`, updatedGame.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['scratchCards'] });
            toast.success("Game updated successfully!");
            setIsBuilderMode(false);
        },
        onError: (error) => {
            const message = error.response?.data?.message || 'Failed to update game.';
            toast.error(`Error: ${message}`);
        }
    });

    const deleteGameMutation = useMutation({
        mutationFn: (gameId) => api.delete(`/scratch-cards/${gameId}`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['scratchCards'] });
            toast.success("Game deleted.");
        },
        onError: () => toast.error("Failed to delete game.")
    });

    const toggleActiveMutation = useMutation({
        mutationFn: ({ id, isActive }) => api.put(`/scratch-cards/${id}`, { isActive }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['scratchCards'] });
            toast.success("Game status updated.");
        },
        onError: () => toast.error("Failed to update status.")
    });

    // Handlers
    const handleCreateNew = () => {
        setGameName('');
        setUrlSlug(''); 
        setSeoTitle(''); 
        setSeoDescription('');
        setMainPhotoUrl(''); 
        setFaviconUrl('');
        setIsActive(true);
        setEditingGame(null); 
        setPrizes(initialPrizeTemplate.map(p => ({ 
            ...p, 
            id: `p_${Date.now()}_${Math.random().toString(36).substr(2, 4)}` 
        })));
        setActivePreviewId(initialPrizeTemplate[0].id);
        setPreviewRevealed(false);
        setIsBuilderMode(true);
    };

    const handleEditGame = (game) => {
        setEditingGame(game);
        setGameName(game.name);
        setUrlSlug(game.urlSlug || '');
        setSeoTitle(game.seoTitle || ''); 
        setSeoDescription(game.seoDescription || '');
        setMainPhotoUrl(game.mainPhotoUrl || ''); 
        setFaviconUrl(game.faviconUrl || '');
        setIsActive(game.isActive);
        setPrizes(game.prizes);
        setActivePreviewId(game.prizes[0]?.id || initialPrizeTemplate[0].id);
        setPreviewRevealed(false);
        setIsBuilderMode(true);
    };

    const handleAddPrize = () => {
        const newId = `p_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`;
        const newPrize = { 
            id: newId, 
            title: 'New Outcome', 
            description: 'Description goes here', 
            icon: 'Gift', 
            color: 'purple', 
            code: '',
            probability: 0 
        };
        setPrizes(current => [...current, newPrize]);
        setActivePreviewId(newId);
        setPreviewRevealed(false);
    };

    const handleUpdatePrize = (id, field, value) => {
        setPrizes(current => current.map(p => p.id === id ? { ...p, [field]: value } : p));
    };

    const handleDeletePrize = (id) => {
        if (prizes.length <= 1) {
            toast.error("A game must have at least one outcome.");
            return;
        }
        const filtered = prizes.filter(p => p.id !== id);
        setPrizes(filtered);
        if (activePreviewId === id) {
            setActivePreviewId(filtered[0].id);
            setPreviewRevealed(false);
        }
    };

    const handleSlugChange = (e) => {
        const safeSlug = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '').replace(/-+/g, '-');
        setUrlSlug(safeSlug);
    };

    const handleCopyPublicUrl = (path, gameId) => {
        const baseUrl = window.location.origin;
        const fullUrl = `${baseUrl}${path}`;
        
        navigator.clipboard.writeText(fullUrl).then(() => {
            setCopiedGameId(gameId);
            toast.success("Public URL copied to clipboard!");
            setTimeout(() => setCopiedGameId(null), 2000);
        }).catch(err => {
            console.error('Failed to copy text: ', err);
            toast.error("Failed to copy URL. Please copy manually.");
        });
    };

    const handleSaveGame = () => {
        if (!gameName.trim()) {
            toast.error("Please give your game a name.");
            return;
        }
        
        if (isActive && !urlSlug.trim()) {
             toast.error("An active game requires a unique URL Slug.");
             return;
        }
        if (isActive && urlSlug.trim().length < 3) {
             toast.error("The URL slug must be at least 3 characters long.");
             return;
        }
        
        const totalProbability = prizes.reduce((sum, p) => sum + (parseInt(p.probability) || 0), 0);
        if (totalProbability !== 100) {
            toast.error("Total probability must equal 100%. Please adjust.");
            return;
        }

        const gameData = {
            name: gameName,
            prizes: prizes.map(p => ({
                ...p,
                probability: parseInt(p.probability)
            })),
            isActive,
            urlSlug: isActive ? urlSlug.trim() : undefined,
            seoTitle: seoTitle.trim(),
            seoDescription: seoDescription.trim(),
            mainPhotoUrl,
            faviconUrl
        };

        if (editingGame) {
            updateGameMutation.mutate({ id: editingGame._id, data: gameData });
        } else {
            createGameMutation.mutate(gameData);
        }
    };

    const getActivePreviewPrize = () => {
        return prizes.find(p => p.id === activePreviewId) || prizes[0];
    };

    const getMarketingCoachMessage = useCallback(() => {
        const userName = user?.contactPersonName || 'Marketing Pro';
        const activeGames = games.filter(g => g.isActive).length;
        const draftGames = games.length - activeGames;
        const totalPlays = games.reduce((sum, g) => sum + (g.playCount || 0), 0);

        if (games.length === 0) {
            return {
                icon: <Sparkles className="text-amber-500" />,
                headline: `Ready to gamify your marketing, ${userName}?`,
                subtext: "Create your first Scratch Card game today. It's a fun, interactive way to capture new leads!",
                colorClass: "border-amber-400 bg-amber-50 dark:bg-amber-900/20"
            };
        }
        if (activeGames === 0 && draftGames > 0) {
            return {
                icon: <Zap className="text-indigo-500" />,
                headline: `Almost there, ${userName}!`,
                subtext: `You have ${draftGames} draft game${draftGames > 1 ? 's' : ''} ready to go. Activate one to start engaging your audience.`,
                colorClass: "border-indigo-400 bg-indigo-50 dark:bg-indigo-900/20"
            };
        }
        if (totalPlays > 50) {
            return {
                icon: <Trophy className="text-purple-500" />,
                headline: `You're crushing it!`,
                subtext: `Your games have been played ${totalPlays} times! Ensure you're following up with those new leads in the Leads section.`,
                colorClass: "border-purple-400 bg-purple-50 dark:bg-purple-900/20"
            };
        }
        if (activeGames > 0) {
            return {
                icon: <Globe className="text-emerald-500" />,
                headline: `Your campaigns are live!`,
                subtext: `Don't forget to share your public game links on social media and email newsletters to boost traffic.`,
                colorClass: "border-emerald-400 bg-emerald-50 dark:bg-emerald-900/20"
            };
        }
        return {
            icon: <Gift className="text-pink-500" />,
            headline: `Gamification boosts conversion.`,
            subtext: "Manage your scratch cards below to keep your customers engaged and happy.",
            colorClass: "border-pink-400 bg-pink-50 dark:bg-pink-900/20"
        };
    }, [games, user]);

    const coachMessage = useMemo(() => getMarketingCoachMessage(), [getMarketingCoachMessage]);

    const totalProbability = prizes.reduce((sum, p) => sum + (parseInt(p.probability) || 0), 0);
    const isProbabilityValid = totalProbability === 100;

    // View States
    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-slate-50 dark:bg-[#0B1120] transition-colors duration-300">
                <Loader2 className="w-12 h-12 animate-spin text-indigo-500 mb-4" />
                <span className="text-xs font-extrabold tracking-widest text-slate-400 dark:text-slate-500 uppercase animate-pulse">Loading Games...</span>
            </div>
        );
    }
    
    if (isError) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-[#0B1120]">
                <div className="p-8 text-center bg-white dark:bg-slate-800 text-rose-700 dark:text-rose-400 border border-rose-200 dark:border-rose-800/50 rounded-3xl shadow-xl max-w-md">
                    <AlertCircle className="w-12 h-12 mx-auto mb-4 text-rose-500 opacity-80" />
                    <h1 className="text-2xl font-black mb-2 text-slate-900 dark:text-white">Error Loading Games</h1>
                    <p className="font-medium text-slate-500">Could not fetch scratch card games. Please check your network or try logging in again.</p>
                </div>
            </div>
        );
    }

    // --- RENDER: DASHBOARD LIST ---
    if (!isBuilderMode) {
        return (
            <div className="p-4 sm:p-8 min-h-screen bg-slate-50 dark:bg-[#0B1120] text-slate-800 dark:text-slate-100 transition-colors duration-300">
                
                {/* Marketing Coach Banner */}
                <div className={`p-6 sm:p-8 rounded-3xl shadow-sm mb-8 flex flex-col sm:flex-row items-start sm:items-center gap-5 border border-transparent ${coachMessage.colorClass}`}>
                    <div className="flex-shrink-0 bg-white/60 dark:bg-black/20 p-4 rounded-2xl shadow-sm">
                        {React.cloneElement(coachMessage.icon, { size: 32 })}
                    </div>
                    <div>
                        <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-slate-900 dark:text-white mb-1.5">
                            {coachMessage.headline}
                        </h2>
                        <p className="font-bold text-slate-600 dark:text-slate-300 text-sm sm:text-base">
                            {coachMessage.subtext}
                        </p>
                    </div>
                </div>

                <div className="max-w-7xl mx-auto">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-5 animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <div>
                            <h1 className="text-3xl sm:text-4xl font-black text-slate-900 dark:text-white tracking-tight">Scratch Cards</h1>
                            <p className="font-bold text-slate-500 dark:text-slate-400 mt-2 uppercase tracking-wide text-xs">Manage customer engagement & instant-win campaigns</p>
                        </div>
                        <button 
                            onClick={handleCreateNew}
                            className="flex items-center justify-center px-6 py-3.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl shadow-sm hover:shadow-md transition-all active:scale-95 font-bold disabled:opacity-50 w-full sm:w-auto"
                            disabled={createGameMutation.isPending || updateGameMutation.isPending}
                        >
                            <Plus className="w-5 h-5 mr-2" /> Create New Game
                        </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-in fade-in slide-in-from-bottom-4 duration-500 delay-100 fill-mode-both">
                        {games.length === 0 ? (
                             <div className="md:col-span-3 flex flex-col items-center justify-center py-20 px-6 bg-white dark:bg-slate-800 rounded-3xl shadow-sm border border-slate-200/60 dark:border-slate-700/50">
                                <div className="p-5 bg-slate-50 dark:bg-slate-900/50 rounded-full mb-5 shadow-inner">
                                    <Gift className="w-12 h-12 text-slate-400" />
                                </div>
                                <p className="text-xl font-black text-slate-900 dark:text-white mb-2">No Scratch Card Games Found</p>
                                <p className="font-medium text-slate-500 dark:text-slate-400 text-center">Click 'Create New Game' to launch your first interactive campaign!</p>
                             </div>
                        ) : (
                            games.map(game => (
                                <div key={game._id} className="group bg-white dark:bg-slate-800 rounded-3xl shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border border-slate-200/60 dark:border-slate-700/50 flex flex-col overflow-hidden">
                                    <div className="p-6 sm:p-8 flex-1">
                                        <div className="flex justify-between items-start mb-6">
                                            <div className="p-3.5 bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-500/10 dark:to-purple-500/10 border border-indigo-100/50 dark:border-indigo-500/20 rounded-2xl shadow-sm">
                                                <Sparkles className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <span className={`px-3 py-1.5 text-xs font-bold rounded-lg border transition-colors shadow-sm ${game.isActive ? 'bg-emerald-50 border-emerald-200/50 text-emerald-700 dark:bg-emerald-500/10 dark:border-emerald-800/50 dark:text-emerald-400' : 'bg-slate-50 border-slate-200/80 text-slate-600 dark:bg-slate-700/50 dark:border-slate-600 dark:text-slate-300'}`}>
                                                    {game.isActive ? 'Active' : 'Draft'}
                                                </span>
                                                <button
                                                    onClick={() => toggleActiveMutation.mutate({ id: game._id, isActive: !game.isActive })}
                                                    title={game.isActive ? "Deactivate Game" : "Activate Game"}
                                                    disabled={toggleActiveMutation.isPending}
                                                    className="p-1.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-400 hover:text-indigo-600 dark:bg-slate-700 dark:border-slate-600 dark:hover:text-indigo-400 transition-colors disabled:opacity-50 shadow-sm active:scale-95"
                                                >
                                                     <RotateCcw className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                        <h3 className="text-xl font-black tracking-tight text-slate-900 dark:text-white mb-2">{game.name}</h3>
                                        <p className="text-sm font-bold text-slate-500 dark:text-slate-400 mb-6">
                                            <span className="text-indigo-600 dark:text-indigo-400">{game.playCount || 0}</span> total plays
                                        </p>
                                        
                                        {game.isActive && game.publicUrl && (
                                            <div className="mb-2 flex items-center bg-slate-50 dark:bg-slate-900/50 p-2.5 rounded-xl text-xs border border-slate-200/60 dark:border-slate-700/50 shadow-inner">
                                                <Link className="w-4 h-4 mx-2 text-indigo-500 flex-shrink-0" />
                                                <span className="text-slate-600 dark:text-slate-300 truncate font-mono font-bold">
                                                     {game.publicUrl}
                                                </span>
                                                <button
                                                    onClick={() => handleCopyPublicUrl(game.publicUrl, game._id)}
                                                    className={`ml-auto p-1.5 transition-all duration-200 rounded-lg ${copiedGameId === game._id ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-400' : 'hover:bg-slate-200 text-slate-400 hover:text-indigo-600 dark:hover:bg-slate-700 dark:hover:text-indigo-400'}`}
                                                    title={copiedGameId === game._id ? "Copied!" : "Copy Public Link"}
                                                >
                                                    {copiedGameId === game._id ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                    <div className="px-6 py-4 bg-slate-50 dark:bg-slate-900/60 border-t border-slate-100 dark:border-slate-700/50 flex gap-3">
                                        <button
                                            onClick={() => setViewingResultsGameId(game._id)}
                                            className="flex-1 flex items-center justify-center px-4 py-2.5 text-sm font-bold text-indigo-700 dark:text-indigo-300 bg-white border border-indigo-200 dark:bg-slate-800 dark:border-indigo-800 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded-xl transition-colors shadow-sm active:scale-95"
                                        >
                                            <Users className="w-4 h-4 mr-2" /> Results
                                        </button>
                                        <div className="flex gap-2">
                                            <button 
                                                onClick={() => handleEditGame(game)}
                                                className="p-2.5 bg-white border border-slate-200 text-slate-500 hover:text-indigo-600 hover:bg-slate-50 dark:bg-slate-800 dark:border-slate-700 dark:hover:bg-slate-700 dark:hover:text-indigo-400 rounded-xl transition-colors shadow-sm active:scale-95"
                                                title="Edit Game"
                                            >
                                                <Edit2 className="w-4 h-4" />
                                            </button>
                                            <button 
                                                onClick={() => deleteGameMutation.mutate(game._id)}
                                                className="p-2.5 bg-white border border-slate-200 text-slate-400 hover:text-rose-600 hover:bg-rose-50 hover:border-rose-200 dark:bg-slate-800 dark:border-slate-700 dark:hover:bg-rose-900/20 dark:hover:text-rose-400 dark:hover:border-rose-800/50 rounded-xl transition-colors shadow-sm active:scale-95"
                                                title="Delete Game"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                <ResultsModal 
                    gameId={viewingResultsGameId} 
                    isOpen={!!viewingResultsGameId} 
                    onClose={() => setViewingResultsGameId(null)} 
                    formatDate={formatDate}
                />
            </div>
        );
    }

    // --- RENDER: GAME BUILDER ---
    const isSaving = createGameMutation.isPending || updateGameMutation.isPending;
    const publicUrlHint = urlSlug.trim() ? `${window.location.origin}/public/scratch-card/${urlSlug.trim()}` : 'A unique slug is required for active games.';

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-[#0B1120] text-slate-800 dark:text-slate-100 transition-colors duration-300">
            
            {/* STICKY HEADER BAR */}
            <div className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border-b border-slate-200/60 dark:border-slate-800/60 px-4 sm:px-8 py-4 sticky top-0 z-30 shadow-sm transition-colors">
                <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
                    <div className="flex items-center w-full md:w-auto">
                        <button 
                            onClick={() => setIsBuilderMode(false)} 
                            className="mr-4 p-2.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors disabled:opacity-50 active:scale-95"
                            disabled={isSaving}
                        >
                            <ArrowLeft className="w-5 h-5 text-slate-600 dark:text-slate-300" />
                        </button>
                        <div>
                            <h2 className="text-xl font-black text-slate-900 dark:text-white tracking-tight">
                                {editingGame ? 'Edit Game' : 'New Game Builder'}
                            </h2>
                            <span className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Design your campaign</span>
                        </div>
                    </div>
                    <div className="flex items-center gap-4 w-full md:w-auto justify-end">
                        <label className="flex items-center space-x-3 text-sm font-bold text-slate-700 dark:text-slate-300 cursor-pointer bg-slate-100 dark:bg-slate-800 px-4 py-2 rounded-xl">
                            <input
                                type="checkbox"
                                checked={isActive}
                                onChange={(e) => setIsActive(e.target.checked)}
                                className="h-4 w-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500 dark:bg-slate-700 dark:border-slate-600 transition-colors"
                                disabled={isSaving}
                            />
                            <span>{isActive ? 'Active' : 'Draft'}</span>
                        </label>
                        <button 
                            onClick={() => setIsBuilderMode(false)}
                            className="px-5 py-2.5 text-sm font-bold text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-all active:scale-95 disabled:opacity-50"
                            disabled={isSaving}
                        >
                            Cancel
                        </button>
                        <button 
                            onClick={handleSaveGame}
                            className="px-6 py-2.5 text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl shadow-sm hover:shadow-md transition-all active:scale-95 flex items-center disabled:opacity-50 disabled:shadow-none"
                            disabled={!isProbabilityValid || (isActive && !urlSlug.trim()) || isSaving}
                        >
                            {isSaving ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <Save className="w-4 h-4 mr-2" />
                            )}
                            {editingGame ? 'Save Changes' : 'Create Game'}
                        </button>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto p-4 sm:p-8 animate-in fade-in duration-500">
                <div className="flex flex-col lg:flex-row gap-8">
                    
                    {/* --- LEFT COLUMN: CONFIGURATION --- */}
                    <div className="flex-1 space-y-8">
                        
                        {/* 1. GAME INFO */}
                        <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-sm border border-slate-200/60 dark:border-slate-700/50 p-6 sm:p-8">
                            <h3 className="text-lg font-black tracking-tight text-slate-900 dark:text-white mb-6 flex items-center gap-3">
                                <div className="w-8 h-8 rounded-xl bg-indigo-50 dark:bg-indigo-500/10 flex items-center justify-center text-indigo-600 dark:text-indigo-400 font-black shadow-inner">1</div>
                                Campaign Details
                            </h3>
                            <div className="space-y-6">
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-1.5 uppercase tracking-wider">Internal Game Name</label>
                                    <input 
                                        type="text" 
                                        className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all font-bold shadow-sm"
                                        placeholder="e.g. Summer Discount Blast 2024"
                                        value={gameName}
                                        onChange={(e) => setGameName(e.target.value)}
                                        disabled={isSaving}
                                    />
                                </div>
                                {/* URL SLUG FIELD */}
                                <div className={isActive ? 'opacity-100 transition-opacity' : 'opacity-50 pointer-events-none'}>
                                    <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-1.5 flex items-center justify-between uppercase tracking-wider">
                                         <span>Public URL Slug {isActive && <span className="text-rose-500 ml-1">*</span>}</span>
                                    </label>
                                    <div className="relative shadow-sm rounded-xl overflow-hidden">
                                         <span className="absolute left-0 top-0 h-full flex items-center pl-4 bg-slate-100 dark:bg-slate-800 border-r border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 text-sm font-mono font-medium z-10 w-[170px]">/public/scratch/</span>
                                         <input 
                                             type="text" 
                                             className="w-full pl-[185px] pr-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 text-indigo-600 dark:text-indigo-400 font-black focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all font-mono"
                                             placeholder="your-custom-slug"
                                             value={urlSlug}
                                             onChange={handleSlugChange}
                                             disabled={isSaving || !isActive}
                                         />
                                    </div>
                                    <p className={`mt-2 text-xs font-medium ${isActive ? 'text-slate-500 dark:text-slate-400' : 'text-slate-400 dark:text-slate-600'}`}>
                                        Live Link: <code className={`font-mono text-xs ${urlSlug.trim() ? 'font-bold text-indigo-600 dark:text-indigo-400' : ''}`}>{publicUrlHint}</code>
                                    </p>
                                </div>

                                {/* VISUAL ASSETS SECTION */}
                                <div className="pt-6 border-t border-slate-100 dark:border-slate-700/50 grid grid-cols-1 sm:grid-cols-2 gap-6">
                                    <ImageUploadInput 
                                        label="Main Hero Photo (Optional)" 
                                        value={mainPhotoUrl} 
                                        onChange={setMainPhotoUrl} 
                                        id="mainPhotoInput"
                                    />
                                    <ImageUploadInput 
                                        label="Browser Favicon (Optional)" 
                                        value={faviconUrl} 
                                        onChange={setFaviconUrl} 
                                        id="faviconInput"
                                    />
                                </div>

                                {/* SEO & SHARING SECTION */}
                                <div className="pt-6 border-t border-slate-100 dark:border-slate-700/50">
                                    <label className="block text-sm font-black tracking-tight text-slate-900 dark:text-white mb-4 flex items-center">
                                        <div className="p-1.5 bg-blue-50 dark:bg-blue-500/10 rounded-lg mr-2">
                                            <Globe className="w-4 h-4 text-blue-500" />
                                        </div>
                                        SEO & Social Sharing
                                    </label>
                                    
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-1.5 uppercase tracking-wider">Page Title (Meta Title)</label>
                                            <input 
                                                type="text" 
                                                className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 text-slate-900 dark:text-slate-100 text-sm font-bold focus:ring-2 focus:ring-indigo-500 outline-none shadow-sm transition-all"
                                                placeholder="e.g. Win Big with Our Summer Sale!"
                                                value={seoTitle}
                                                onChange={(e) => setSeoTitle(e.target.value)}
                                                disabled={isSaving}
                                            />
                                        </div>
                                        
                                        <div>
                                            <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-1.5 uppercase tracking-wider">Description (Meta Description)</label>
                                            <textarea 
                                                className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 text-slate-900 dark:text-slate-100 text-sm font-medium focus:ring-2 focus:ring-indigo-500 outline-none resize-none h-24 shadow-sm transition-all custom-scrollbar"
                                                placeholder="e.g. Play our free scratch card game to reveal exclusive discounts and prizes. Limited time only!"
                                                value={seoDescription}
                                                onChange={(e) => setSeoDescription(e.target.value)}
                                                disabled={isSaving}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* 2. OUTCOMES EDITOR */}
                        <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-sm border border-slate-200/60 dark:border-slate-700/50 p-6 sm:p-8">
                            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
                                 <h3 className="text-lg font-black tracking-tight text-slate-900 dark:text-white flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-xl bg-purple-50 dark:bg-purple-500/10 flex items-center justify-center text-purple-600 dark:text-purple-400 font-black shadow-inner">2</div>
                                    Prize Outcomes
                                </h3>
                                <button 
                                    onClick={handleAddPrize}
                                    className="text-sm text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 font-bold flex items-center px-4 py-2 bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-500/10 dark:hover:bg-indigo-500/20 rounded-xl transition-all active:scale-95 disabled:opacity-50"
                                    disabled={isSaving}
                                >
                                    <Plus className="w-4 h-4 mr-2" /> Add Outcome
                                </button>
                            </div>

                            <div className="space-y-4">
                                {prizes.map((prize, index) => (
                                    <div 
                                        key={prize.id} 
                                        onClick={() => { setActivePreviewId(prize.id); setPreviewRevealed(false); }}
                                        className={`relative border-2 rounded-2xl p-5 cursor-pointer transition-all duration-200 ${activePreviewId === prize.id ? 'border-indigo-500 bg-indigo-50/30 dark:bg-indigo-900/10 shadow-md transform scale-[1.01]' : 'border-slate-200 dark:border-slate-700 hover:border-indigo-300 dark:hover:border-indigo-700 hover:bg-slate-50 dark:hover:bg-slate-800/80 shadow-sm'}`}
                                    >
                                        <div className="flex justify-between items-start mb-5">
                                            <div className="flex items-center">
                                                <span className="bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-xs font-black uppercase tracking-widest px-3 py-1.5 rounded-lg mr-3">
                                                    Outcome #{index + 1}
                                                </span>
                                                <span className={`text-xs font-bold uppercase tracking-wider px-2 py-1 rounded-md ${isProbabilityValid ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400' : 'bg-rose-50 text-rose-600 dark:bg-rose-500/10 dark:text-rose-400'}`}>
                                                    {prize.probability}% Chance
                                                </span>
                                            </div>
                                            <button 
                                                onClick={(e) => { e.stopPropagation(); handleDeletePrize(prize.id); }} 
                                                className="text-slate-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-500/10 p-2 rounded-lg transition-colors disabled:opacity-50"
                                                disabled={isSaving || prizes.length <= 1}
                                                title="Delete Outcome"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>

                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                            <div>
                                                <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-1.5 uppercase tracking-wider">Title (Big Text)</label>
                                                <input 
                                                    type="text" 
                                                    className="w-full text-sm font-bold px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 focus:ring-2 focus:ring-indigo-500 outline-none disabled:opacity-50 transition-all shadow-sm"
                                                    value={prize.title}
                                                    onChange={(e) => handleUpdatePrize(prize.id, 'title', e.target.value)}
                                                    placeholder="e.g. 10% OFF"
                                                    disabled={isSaving}
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-1.5 uppercase tracking-wider">Description</label>
                                                <input 
                                                    type="text" 
                                                    className="w-full text-sm font-medium px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 focus:ring-2 focus:ring-indigo-500 outline-none disabled:opacity-50 transition-all shadow-sm"
                                                    value={prize.description}
                                                    onChange={(e) => handleUpdatePrize(prize.id, 'description', e.target.value)}
                                                    placeholder="e.g. Minimum spend £50"
                                                    disabled={isSaving}
                                                />
                                            </div>
                                            
                                            {/* Icon & Style Picker */}
                                            <div className="sm:col-span-2 bg-slate-50 dark:bg-slate-900/50 p-4 rounded-xl border border-slate-100 dark:border-slate-700/50">
                                                <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-2.5 uppercase tracking-wider">Icon & Theme</label>
                                                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                                                    <div className="flex gap-2 overflow-x-auto pb-1 custom-scrollbar">
                                                        {Object.keys(ICON_MAP).map(iconKey => {
                                                            const Ico = ICON_MAP[iconKey];
                                                            const isSelected = prize.icon === iconKey;
                                                            return (
                                                                <button
                                                                    key={iconKey}
                                                                    type="button"
                                                                    onClick={() => handleUpdatePrize(prize.id, 'icon', iconKey)}
                                                                    className={`p-2 rounded-xl transition-all disabled:opacity-50 flex-shrink-0 ${isSelected ? 'bg-white dark:bg-slate-700 shadow-md text-indigo-600 dark:text-indigo-400 ring-1 ring-indigo-200 dark:ring-indigo-700' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-200/50 dark:hover:text-slate-200 dark:hover:bg-slate-700/50'}`}
                                                                    title={iconKey}
                                                                    disabled={isSaving}
                                                                >
                                                                    <Ico className="w-5 h-5" />
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                    <div className="hidden sm:block w-px h-8 bg-slate-200 dark:bg-slate-700"></div>
                                                    <div className="flex gap-3">
                                                        {COLORS.map(c => (
                                                            <button
                                                                key={c.name}
                                                                type="button"
                                                                onClick={() => handleUpdatePrize(prize.id, 'color', c.name)}
                                                                className={`w-6 h-6 rounded-full transition-transform disabled:opacity-50 ${c.bg} border-2 ${prize.color === c.name ? `ring-2 ring-offset-2 dark:ring-offset-slate-800 ${c.ring} border-transparent scale-110` : 'border-slate-300 dark:border-slate-600'}`}
                                                                disabled={isSaving}
                                                            />
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>

                                            <div>
                                                <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-1.5 uppercase tracking-wider">Discount Code <span className="opacity-70 font-medium lowercase">(Optional)</span></label>
                                                <div className="relative shadow-sm rounded-xl overflow-hidden">
                                                    <Tag className="absolute left-3.5 top-3 w-4 h-4 text-slate-400" />
                                                    <input 
                                                        type="text" 
                                                        className="w-full text-sm font-bold pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 font-mono uppercase disabled:opacity-50 focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                                                        value={prize.code}
                                                        onChange={(e) => handleUpdatePrize(prize.id, 'code', e.target.value)}
                                                        placeholder="CODE"
                                                        disabled={isSaving}
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-1.5 uppercase tracking-wider">Win Chance (%)</label>
                                                <div className="relative shadow-sm rounded-xl overflow-hidden">
                                                    <input 
                                                        type="number" 
                                                        className="w-full text-sm font-black text-indigo-600 dark:text-indigo-400 pl-4 pr-10 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 disabled:opacity-50 focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                                                        value={prize.probability}
                                                        onChange={(e) => handleUpdatePrize(prize.id, 'probability', e.target.value)}
                                                        min="0" max="100"
                                                        disabled={isSaving}
                                                    />
                                                    <span className="absolute right-4 top-2.5 text-sm font-black text-slate-400">%</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className={`mt-8 flex items-center p-4 rounded-2xl shadow-sm border ${isProbabilityValid ? 'bg-emerald-50 border-emerald-100 text-emerald-800 dark:bg-emerald-500/10 dark:border-emerald-800/30 dark:text-emerald-300' : 'bg-rose-50 border-rose-100 text-rose-800 dark:bg-rose-500/10 dark:border-rose-800/30 dark:text-rose-300'}`}>
                                {isProbabilityValid ? <CheckCircle className="w-6 h-6 mr-3" /> : <AlertCircle className="w-6 h-6 mr-3" />}
                                <span className="text-sm font-bold">Total Probability: <span className="text-lg ml-1 font-black">{totalProbability}%</span> {isProbabilityValid ? '(Perfect!)' : '(Must exactly equal 100%)'}</span>
                            </div>
                        </div>
                    </div>

                    {/* --- RIGHT COLUMN: LIVE PREVIEW --- */}
                    <div className="w-full lg:w-96 flex-shrink-0">
                        <div className="sticky top-28 space-y-6">
                            <div className="bg-white dark:bg-slate-800 p-6 sm:p-8 rounded-3xl shadow-lg border border-slate-200/60 dark:border-slate-700/50 flex flex-col items-center">
                                <div className="text-center mb-8 w-full border-b border-slate-100 dark:border-slate-700/50 pb-4">
                                    <h3 className="text-lg font-black tracking-tight text-slate-900 dark:text-white flex items-center justify-center">
                                        <RotateCcw className="w-5 h-5 mr-2 text-indigo-500" /> Live Preview
                                    </h3>
                                    <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mt-1.5">Try scratching the card!</p>
                                </div>
                                
                                <div className="flex justify-center mb-4">
                                    <ScratchCardPreview 
                                        prize={getActivePreviewPrize()}
                                        isRevealed={previewRevealed}
                                        onReveal={() => setPreviewRevealed(true)}
                                        onReset={() => setPreviewRevealed(false)}
                                    />
                                </div>

                                <div className="mt-8 bg-slate-50 dark:bg-slate-900/60 rounded-2xl p-4 w-full border border-slate-100 dark:border-slate-700/50 shadow-inner">
                                    <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-3">Currently Editing</p>
                                    <div className="flex items-center gap-4">
                                        {(() => {
                                            const Ico = ICON_MAP[getActivePreviewPrize().icon] || Gift;
                                            const col = COLORS.find(c => c.name === getActivePreviewPrize().color);
                                            return (
                                                <div className={`p-3 rounded-xl shadow-sm ${col?.bg || 'bg-slate-100'} ${col?.text || 'text-slate-600'}`}>
                                                    <Ico className="w-6 h-6" />
                                                </div>
                                            );
                                        })()}
                                        <div>
                                            <p className="text-base font-black tracking-tight text-slate-900 dark:text-white">{getActivePreviewPrize().title || 'Untitled'}</p>
                                            <p className="text-xs font-medium text-slate-500 truncate max-w-[180px]">{getActivePreviewPrize().description || 'No description'}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Data Flow Note */}
                            <div className="p-6 rounded-3xl bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800/50 text-indigo-900 dark:text-indigo-200 shadow-sm">
                                <div>
                                    <p className="font-black text-sm mb-3 flex items-center uppercase tracking-wider"><Info className="w-5 h-5 mr-2 opacity-80" /> Data Flow</p>
                                    <ul className="list-disc list-outside ml-4 space-y-2 text-sm font-medium opacity-90">
                                        <li>
                                            <strong>New Leads:</strong> Submissions from new users will automatically appear in the <span className="font-bold underline decoration-indigo-300">Lead Page</span>.
                                        </li>
                                        <li>
                                            <strong>Existing Customers:</strong> Submissions will automatically create a request in the <span className="font-bold underline decoration-indigo-300">Quote Management Page</span>.
                                        </li>
                                    </ul>
                                </div>
                                <div className="mt-5 pt-4 border-t border-indigo-200 dark:border-indigo-700/50">
                                    <p className="text-xs font-bold opacity-80 leading-relaxed">
                                        <span className="font-black tracking-wider uppercase">Pro Tip:</span> Once saved, include this campaign link in your email blasts, SMS campaigns, or social media bios to maximize engagement.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default ScratchCardManager;
export { ICON_MAP, COLORS };